import { useEffect, useState } from "react";
import { Gift, Clock, Trophy } from "lucide-react";
import { api } from "@/lib/api";
import { useLang } from "@/i18n";

// Title-screen banner: this week's winning gift + live countdown to when the
// highest-score competition ends (Sunday 6PM Nicaragua time).
export default function WeeklyPrizeBanner() {
  const { lang } = useLang();
  const en = lang === "en";
  const [info, setInfo] = useState(null);
  const [remain, setRemain] = useState(null);

  useEffect(() => { api.tournamentInfo().then((r) => setInfo(r.data)).catch(() => {}); }, []);

  useEffect(() => {
    if (!info?.ends_at) return;
    const end = new Date(info.ends_at).getTime();
    const tick = () => {
      const ms = end - Date.now();
      if (ms <= 0) { setRemain({ d: 0, h: 0, m: 0, s: 0 }); return; }
      setRemain({
        d: Math.floor(ms / 86400000),
        h: Math.floor((ms % 86400000) / 3600000),
        m: Math.floor((ms % 3600000) / 60000),
        s: Math.floor((ms % 60000) / 1000),
      });
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [info]);

  if (!info) return null;

  const prize = info.prize_is_default
    ? (en ? "🏆 Top player of the week — featured on the leaderboard!" : "🏆 Mejor jugador de la semana — ¡destacado en la tabla de posiciones!")
    : info.prize;
  const seg = (val, unit) => (
    <span className="inline-flex items-baseline gap-0.5">
      <b className="font-pixel text-[#FFEB3B] text-[11px] tabular-nums">{String(val).padStart(2, "0")}</b>
      <span className="font-vt text-xs text-white/55">{unit}</span>
    </span>
  );

  return (
    <div
      data-testid="weekly-prize-banner"
      className="max-w-md mx-auto px-2.5 py-1 rounded-xl bg-black/60 backdrop-blur-md ring-1 ring-[#FFB300]/40 flex flex-col gap-0.5"
    >
      {/* Top row: countdown + competing */}
      <div className="flex items-center justify-center gap-4">
        <div className="flex items-center gap-2 shrink-0" data-testid="weekly-countdown">
          <Clock size={14} className="text-[#FF5722]" />
          {remain ? (
            <span className="flex items-center gap-1.5">
              {remain.d > 0 && seg(remain.d, "d")}
              {seg(remain.h, "h")}{seg(remain.m, "m")}{seg(remain.s, "s")}
            </span>
          ) : null}
        </div>
        {info.competing > 0 && (
          <div className="flex items-center gap-1 shrink-0 font-vt text-xs text-[#FFD54F]" data-testid="weekly-competing">
            <Trophy size={13} strokeWidth={2.5} /> {info.competing} {en ? "competing" : "compitiendo"}
          </div>
        )}
      </div>

      {/* Bottom row: scrolling prize */}
      <div className="flex items-center gap-2 min-w-0 w-full border-t border-white/10 pt-0.5">
        {info.logo_url ? (
          <img src={`${process.env.REACT_APP_BACKEND_URL}${info.logo_url}`} alt={info.sponsor || "sponsor"} className="h-6 w-6 rounded object-contain bg-white/10 shrink-0" data-testid="weekly-prize-logo" />
        ) : (
          <Gift size={14} strokeWidth={2.5} className="text-[#FFB300] shrink-0" />
        )}
        <div className="overflow-hidden flex-1 min-w-0" data-testid="weekly-prize-text">
          <div className="prize-marquee inline-flex whitespace-nowrap font-vt text-xs text-white/90 leading-tight">
            <span className="pr-12">
              <span className="text-[#FFB300] font-semibold">{en ? "This week's prize:" : "Premio de la semana:"}</span>{" "}
              {prize}
              {info.sponsor ? <span className="text-white/55"> · {en ? "by" : "por"} {info.sponsor}</span> : null}
            </span>
            <span className="pr-12" aria-hidden="true">
              <span className="text-[#FFB300] font-semibold">{en ? "This week's prize:" : "Premio de la semana:"}</span>{" "}
              {prize}
              {info.sponsor ? <span className="text-white/55"> · {en ? "by" : "por"} {info.sponsor}</span> : null}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}
